import { useEffect, useRef, useState, type JSX } from 'react'
import { deskAssetUrl } from './api'
import { sanitizeHtml } from './sanitizeHtml'

// A captured widget, rendered in a shadow root with the desktop app's own
// stylesheet.
//
// Shadow DOM is what makes this work at all. The capture carries the app's
// class names rather than ~9KB of inlined properties per element, so it needs
// the app's CSS to look like anything -- and that CSS is a full application
// stylesheet with its own resets and custom properties, which would wreck this
// page if it were loaded globally. A shadow root scopes it exactly to the
// capture and nothing else.
//
// The sheet is loaded on demand and parsed once, then adopted by every
// capture. Loading it eagerly put 372KB into the bundle for every visitor,
// including the ones looking at a desk with no captures on it at all; a desk
// with five captured widgets now pays for it once, and a desk with none never
// does.
//
// Both sheets are constructed stylesheets (adoptedStyleSheets), never <style>
// elements: the page's CSP allows style elements only from this origin
// (style-src-elem 'self'), which is what stops shared markup from carrying CSS
// selectors of its own. Engines too old for constructed sheets also predate
// style-src-elem, so the <style> fallback below still works for them.

/** Sizing for the frame itself; paint containment keeps the capture inside it. */
const FRAME_CSS =
  ':host{display:block;height:100%;contain:paint}' +
  '.plexi-capture{height:100%;width:100%;overflow:auto;position:relative}'

let sheetPromise: Promise<{ sheets: CSSStyleSheet[] | null; css: string }> | null = null
function loadStyles(): Promise<{ sheets: CSSStyleSheet[] | null; css: string }> {
  if (!sheetPromise) {
    sheetPromise = import('./appStyles.css?raw').then((m) => {
      const css = m.default
      try {
        const app = new CSSStyleSheet()
        app.replaceSync(css)
        const frame = new CSSStyleSheet()
        frame.replaceSync(FRAME_CSS)
        return { sheets: [app, frame], css }
      } catch {
        // Engines without constructable stylesheets fall back to a <style>.
        return { sheets: null, css }
      }
    })
  }
  return sheetPromise
}

export function CaptureFrame({
  apiBase,
  token,
  assetId,
  kind,
  title
}: {
  apiBase: string
  token: string
  assetId: string
  kind: string
  title: string
}): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Served as text/plain, deliberately: the server never hands this back as a
    // document, so it cannot execute on the signal origin.
    void fetch(deskAssetUrl(apiBase, token, assetId))
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => !cancelled && setHtml(t))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [apiBase, token, assetId])

  useEffect(() => {
    const host = hostRef.current
    if (!host || html === null) return
    let cancelled = false
    void loadStyles().then(({ sheets, css }) => {
      if (cancelled || !hostRef.current) return
      const root = hostRef.current.shadowRoot ?? hostRef.current.attachShadow({ mode: 'open' })
      // The capture was sanitised on the desktop, but the server's own check of
      // the bytes is a pattern match (`<img src="x"/onerror=...>` passes it),
      // and anyone with an account can publish a desk of their own. So it is
      // sanitised again here, by the same allowlist as every other piece of
      // shared markup, and inserted as nodes.
      // (A shadow root does not stop an inline event handler from running; the
      // sanitiser and the page's script-src are what do.)
      // The capture is a picture of a widget at its own size; the card it lands
      // in may be smaller. Clip it to the frame rather than let it paint over
      // its neighbours, and let the reader scroll inside it.
      const content = document.createElement('div')
      content.className = 'plexi-capture'
      content.appendChild(sanitizeHtml(html, document))
      if (sheets) {
        root.adoptedStyleSheets = sheets
        root.replaceChildren(content)
      } else {
        const style = document.createElement('style')
        style.textContent = css + FRAME_CSS
        root.replaceChildren(style, content)
      }
    })
    return () => {
      cancelled = true
    }
  }, [html])

  if (failed) {
    return (
      <div className="h-full w-full flex items-center justify-center p-4 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-400">
          {kind} — view unavailable
        </div>
      </div>
    )
  }
  return (
    <div
      ref={hostRef}
      className="safe-html h-full w-full overflow-auto"
      aria-label={`${title || kind} (captured view)`}
    />
  )
}
